// aiComposerKeys — THE pre-send key model of the AI composer, in ONE place.
//
// Why this file exists (2026-09-02, the second half of the '+' attach-menu
// keyboard defect): `workspace/app.js` mounts canvas's `CanvasPromptTextarea`
// at the shell root and overrides `Textarea.onKeydown` at the mount site, to
// route Enter through `homeAiSend` instead of the composer's own `_sendPrompt`.
// A mount-site handler REPLACES the component's own — DOMQL never chains the
// two — so every OTHER key branch the composer declares is dead in the shell
// mount. The first cut re-declared the attach-menu arrow branch inside the
// override, which fixed that one branch and left the MENTION PICKER keys
// (Arrow/Home/End/Enter/Tab/Escape while the '@' / '#' / '##' / '@@' / '+'
// suggestion list is open) still dropped on the home composer.
//
// Enumerating branches per mount is what let the two drift in the first place.
// So both mounts now DELEGATE: `aiComposerKeydown(e, el, s)` owns every key
// that belongs to an open panel, and REPORTS whether it consumed the keystroke.
// Each mount keeps only its own send leg behind that one guard:
//
//   onKeydown: (e, el, s) => {
//     if (aiComposerKeydown(e, el, s)) return      // picker / menu keys
//     if (e.key === 'Enter' && !e.shiftKey) { … }  // this mount's OWN send
//   }
//
// The Enter contract that makes the delegation safe: Enter is consumed here
// ONLY while a mention picker with options is open (it inserts the mention).
// With no picker open the function returns false and the caller's send path
// runs exactly as before — one send, never two.
import { armNamedMenuFocus } from './menuKeyboard.js'
import { mentionRefInsertText } from './mentionRefs.js'
import { aiPanelOf, aiPanelShown, aiPanelOpen, aiPanelDismiss } from './aiPanelFocus.js'

// The composer's DOMQL key, used to walk from a dropdown ROW back to the
// textarea (a row's own `el.node` is the row). The textarea call sites never
// need it — their `el.node` IS the textarea, checked first.
const COMPOSER_KEY = 'CanvasPromptTextarea'

// rAF that survives a windowless runtime (bun test, bypass mode) — a bare
// `requestAnimationFrame` reference THROWS there, which turns a picker Enter
// into an aborted handler in the suite. Falls to a 16ms tick.
const _raf = (fn) =>
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16)

const _textareaOf = (el) => {
  const node = el && el.node
  if (!node) return null
  if (node.tagName === 'TEXTAREA') return node
  return node.closest?.(`[data-key="${COMPOSER_KEY}"]`)?.querySelector?.('textarea') || null
}

// Apply a picked suggestion: splice the insert text over the [start, end)
// trigger span the picker recorded, close the picker, and put the caret after
// the insertion once the value factory has re-painted the node.
//
// `el` may be the textarea element (keyboard pick) or a dropdown ROW element
// (pointer pick); `s` is always the COMPOSER state that holds `mentionPicker`.
export const applyAiMentionPick = (el, s, option) => {
  const p = s && s.mentionPicker
  if (!p) return false
  const text = mentionRefInsertText(p.type, option)
  if (!text) {
    s.update({ mentionPicker: null })
    return true
  }
  const body = s.draft || ''
  const next = body.slice(0, p.start) + text + body.slice(p.end)
  const caret = p.start + text.length
  s.update({ draft: next, mentionPicker: null })
  _raf(() => {
    const ta = _textareaOf(el)
    if (!ta) return
    ta.focus()
    try {
      ta.setSelectionRange(caret, caret)
    } catch (_) {}
  })
  return true
}

// Every key that belongs to an OPEN panel of the AI composer. Returns true
// when the keystroke was consumed, so the caller's own send branch stays
// behind a single `if (…) return`.
export const aiComposerKeydown = (e, el, s) => {
  if (!e || !e.key || !s) return false
  // An open '+' menu owns the arrow keys. The panel is a SIBLING of the
  // textarea, and DOMQL's delegated keydown walks the ANCESTOR chain of
  // `event.target` (smbls delegate.js `installDelegatedEvent`) — a keystroke
  // typed in the textarea can never reach `AttachMenu.onKeydown`. So the
  // textarea hands focus INTO the menu; every later arrow then arrives at the
  // panel itself. ArrowUp enters at the last row, as on the '+' button.
  // Checked before the mention picker: the two are mutually exclusive in
  // practice, and an open menu is the more recent intent.
  if (s.attachOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (typeof e.stopPropagation === 'function') e.stopPropagation()
    armNamedMenuFocus(el, 'AttachMenu', { last: e.key === 'ArrowUp' })
    return true
  }
  const p = s.mentionPicker
  if (!p) return _aiPanelKeys(e, el, s)
  const options = p.options || []
  if (options.length) {
    const n = options.length
    const idx = p.activeIndex || 0
    // Home/End jump to the ends of the suggestion list — the same two keys
    // every other menu in this pass answers. The highlight is state-driven
    // (`activeIndex` re-renders the rows), NOT DOM focus: focus must stay in
    // the textarea so the caret and the typed query survive, which is why this
    // list keeps its own key handling here instead of the shared DOM walker
    // the panel menus use.
    let activeIndex = null
    if (e.key === 'ArrowDown') activeIndex = (idx + 1) % n
    else if (e.key === 'ArrowUp') activeIndex = (idx - 1 + n) % n
    else if (e.key === 'Home') activeIndex = 0
    else if (e.key === 'End') activeIndex = n - 1
    if (activeIndex !== null) {
      if (typeof e.preventDefault === 'function') e.preventDefault()
      s.update({ mentionPicker: { ...p, activeIndex } })
      return true
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (typeof e.preventDefault === 'function') e.preventDefault()
      applyAiMentionPick(el, s, options[idx])
      return true
    }
    if (e.key === 'Escape') {
      if (typeof e.preventDefault === 'function') e.preventDefault()
      // Close the list only. No blur: the caret, the draft and the typed query
      // all stay exactly where the user left them.
      s.update({ mentionPicker: null })
      return true
    }
    return false
  }
  // A picker that is open but still empty (an async type mid-fetch) answers
  // Escape and nothing else.
  if (e.key === 'Escape') {
    if (typeof e.preventDefault === 'function') e.preventDefault()
    s.update({ mentionPicker: null })
    return true
  }
  return false
}

// THE FOCUS PANEL's keys from the field (DESIGN-COMPOSER-FOCUS-POPOVER-
// CONTEXT-INSPECT-1; shared functions/aiPanelFocus.js): ↑ in an EMPTY field
// (the caret has nowhere to go) opens the panel on the row nearest the field;
// Escape puts a painted panel away and keeps the field's focus. Never with a
// modifier, never while a picker or the "+" menu owns the keys (checked by
// the caller's order above), never on a host without the panel.
const _aiPanelKeys = (e, el, s) => {
  if (e.key !== 'ArrowUp' && e.key !== 'Escape') return false
  if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return false
  if (!aiPanelOf(el)) return false
  if (e.key === 'ArrowUp' && !String((s && s.draft) || '').trim()) {
    if (typeof e.preventDefault === 'function') e.preventDefault()
    aiPanelOpen.call(el, 'rows')
    return true
  }
  if (e.key === 'Escape' && aiPanelShown(el)) {
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (typeof e.stopPropagation === 'function') e.stopPropagation()
    aiPanelDismiss.call(el)
    return true
  }
  return false
}
